import { Outlet, Link, useLocation } from "react-router-dom";
// Le signal de presence : sans lui, le support n'envoie jamais son
// « je suis la », et le tableau de bord l'affiche « jamais connecte »
// alors qu'il travaille.
import EtatConnexion from "@/components/EtatConnexion";
import { useAuth } from "@/lib/hooks/useAuth";
import { useEcranEtroit } from "@/lib/hooks/useEcranEtroit";
import { logout } from "@/lib/firebase/auth";
import {
  Activity,
  Wallet,
  LayoutDashboard,
  Store,
  ShoppingCart,
  Package,
  FolderTree,
  MessageSquare,
  FileUp,
  Calculator,
  LogOut,
  ChevronRight,
  TrendingUp,
  Truck,
  Bike,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { COLLECTIONS } from "@/lib/constants";
import NotificationPrompt from "@/components/NotificationPrompt";
import BoutonLangue from "@/components/BoutonLangue";
import NewOrderAlarm from "@/components/NewOrderAlarm";

export default function SupportLayout() {
  // Une seule copie de la page montee (01/11/2026) : voir useEcranEtroit.
  const etroit = useEcranEtroit();
  const { user } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previousCountRef = useRef(-1);
  const audioEnabledRef = useRef(
    localStorage.getItem("audioNotifications") !== "false"
  );

  // 🔔 Demander l'autorisation audio au premier chargement
  useEffect(() => {
    const hasAskedPermission = localStorage.getItem("audioPermissionAsked");
    if (!hasAskedPermission) {
      setTimeout(() => setShowAudioPrompt(true), 2000);
    } else if (hasAskedPermission === "granted" && audioRef.current) {
      audioRef.current.load();
      audioEnabledRef.current = true;
    }
  }, []);

  const handleEnableAudio = () => {
    if (audioRef.current) {
      audioRef.current
        .play()
        .then(() => {
          audioRef.current!.pause();
          audioRef.current!.currentTime = 0;
          audioEnabledRef.current = true;
          localStorage.setItem("audioNotifications", "true");
          localStorage.setItem("audioPermissionAsked", "granted");
          setShowAudioPrompt(false);
        })
        .catch(() => {
          localStorage.setItem("audioNotifications", "false");
          localStorage.setItem("audioPermissionAsked", "denied");
          setShowAudioPrompt(false);
        });
    }
  };

  const handleDenyAudio = () => {
    audioEnabledRef.current = false;
    localStorage.setItem("audioNotifications", "false");
    localStorage.setItem("audioPermissionAsked", "denied");
    setShowAudioPrompt(false);
  };

  const handleLogout = async () => {
    await logout();
  };

  // 🔔 Écouter les messages non lus
  useEffect(() => {
    if (location.pathname === "/support/messaging") {
      setUnreadCount(0);
      previousCountRef.current = -1;
      return;
    }

    const threeDaysAgo = Timestamp.fromDate(
      new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    );

    const q = query(
      collection(db, COLLECTIONS.CONVERSATIONS),
      where("isActive", "==", true),
      where("lastMessageAt", ">=", threeDaysAgo),
      orderBy("lastMessageAt", "desc"),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const total = snapshot.docs.reduce((sum, doc) => {
        const data = doc.data();
        return sum + (data.unreadCount || 0);
      }, 0);

      // 🔔 Jouer le son si nouveau message (ignorer le premier chargement)
      if (previousCountRef.current === -1) {
        // Premier chargement, juste initialiser sans jouer
        previousCountRef.current = total;
      } else if (total > previousCountRef.current && audioEnabledRef.current) {
        // Vraiment un nouveau message
        audioRef.current?.play().catch(() => {});
        previousCountRef.current = total;
      } else {
        previousCountRef.current = total;
      }

      setUnreadCount(total);
    });

    return () => unsubscribe();
  }, [location.pathname]);

  const navItems = [
    { path: "/support/pilotage", label: "Pilotage", icon: Activity },
    { path: "/support/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/support/stores", label: "Stores", icon: Store },
    { path: "/support/orders", label: "Commandes", icon: ShoppingCart },
    // Les livreurs (01/11/2026) : meme page que la direction.
    { path: "/support/livreurs", label: "Livreurs", icon: Bike },
    { path: "/support/suivi", label: "Performance", icon: TrendingUp },
    {
      path: "/support/messaging",
      label: "Messages",
      icon: MessageSquare,
      badge: unreadCount,
    },
    { path: "/support/products", label: "Produits", icon: Package },
    { path: "/support/emballages", label: "Emballages", icon: Package },
    { path: "/support/categories", label: "Catégories", icon: FolderTree },
    { path: "/support/caisse", label: "Caisse", icon: Wallet },
    { path: "/support/import-glovo", label: "Import Glovo", icon: FileUp },
    { path: "/support/comptabilite", label: "Comptabilité", icon: Calculator },
    // Seulement pour le support designe par la direction (18/10/2026).
    ...(user?.accesFournisseurs
      ? [{ path: "/support/fournisseurs", label: "Fournisseurs", icon: Truck }]
      : []),
  ];

  // Menu simplifié pour mobile
  const mobileNavItems = [
    { path: "/support/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/support/orders", label: "Commandes", icon: ShoppingCart },
    {
      path: "/support/messaging",
      label: "Messages",
      icon: MessageSquare,
      badge: unreadCount,
    },
    // Les remboursements a justifier, aussi depuis le telephone (10/10/2026).
    { path: "/support/import-glovo", label: "Rembours.", icon: FileUp },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <audio ref={audioRef} src="/sounds/notification.mp3" preload="auto" />
      
      {/* Notification Permission Prompt */}
      {/* 🚨 Alerte sonore a l'arrivee d'une commande */}
      <NewOrderAlarm lienCommandes="/support/orders" />

      <NotificationPrompt />

      {/* Le bouton de langue, en haut a droite et toujours accessible :
          c'est le support qui traduit le catalogue pour les fournisseurs
          qui ne lisent pas le francais. */}
      <div className="fixed top-3 right-3 z-[9998]">
        <BoutonLangue />
      </div>

      {/* Audio Permission Popup */}
      {showAudioPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Activer les notifications sonores ?
              </h3>
              <p className="text-gray-600 mb-6">
                Recevez une alerte sonore lors de nouveaux messages clients
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDenyAudio}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Non merci
                </button>
                <button
                  onClick={handleEnableAudio}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Activer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Bottom Navigation - Menu simplifié */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-pb shadow-lg">
        <div className="flex items-center justify-around px-2 py-2">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 rounded-lg transition-colors ${
                  isActive
                    ? "text-purple-600 bg-purple-50"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <div className="relative">
                  <Icon className="w-6 h-6" />
                  {/* Badge uniquement si messages non lus */}
                  {item.path === "/support/messaging" && unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </div>
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
          <button
            onClick={handleLogout}
            className="flex-1 flex flex-col items-center justify-center gap-1 py-2 rounded-lg transition-colors text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-6 h-6" />
            <span className="text-xs font-medium">Déco</span>
          </button>
        </div>
      </div>

      {/* Desktop: Sidebar */}
      <div className="hidden md:flex w-full overflow-hidden">
        <aside className="w-72 flex-shrink-0 bg-gradient-to-b from-purple-900 via-purple-800 to-purple-900 min-h-screen flex flex-col shadow-2xl">
          {/* Header */}
          <div className="p-6 border-b border-purple-700/50">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <span className="text-2xl font-bold text-white">N</span>
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">Natura Beldi</h2>
                <p className="text-purple-200 text-xs">Support Dashboard</p>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="p-4 bg-white/5 backdrop-blur-sm mx-4 my-4 rounded-xl border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">
                  {user?.username}
                </p>
                <p className="text-purple-200 text-xs">Support</p>
                {!etroit && <EtatConnexion sombre />}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-2 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all group relative ${
                    isActive
                      ? "bg-white text-purple-900 shadow-lg"
                      : "text-purple-100 hover:bg-white/10"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${isActive ? "text-purple-600" : ""}`}
                  />
                  <span className="font-medium flex-1">{item.label}</span>
                  {item.badge && item.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                  {isActive && (
                    <ChevronRight className="w-4 h-4 text-purple-600" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-purple-700/50">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-purple-100 hover:bg-white/10 rounded-xl transition-all group"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Déconnexion</span>
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-6 pb-20 md:pb-6 overflow-hidden">
          {!etroit && <Outlet />}
        </main>
      </div>

      {/* Mobile: Main Content */}
      <div className="md:hidden pb-20">
        {etroit && <EtatConnexion nom={user?.username} barre />}
        {etroit && <Outlet />}
      </div>
    </div>
  );
}
